import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import {
  GetCustomerType,
  PostCustomerTypeConfiguration,
  PutCustomerTypeConfiguration,
  TPostCustomerTypeConfig,
  TPutCustomerTypeConfig,
} from "api/rpc/2024-04/facilityAdmin/customer/type";
import { GetBookingCategories } from "api/rpc/2024-04/facilityAdmin/teesheet/bookingCategory";

import { showError, showSuccess } from "redux/actions/ui";
import { TCustomerType, TTeesheetCustomerTypeConfig } from "redux/reducers/models/facility";
import { IBookingCategory } from "redux/reducers/models/teetime";
import { useAppDispatch } from "hooks/redux";
import { unsavedChangesExist } from "helpers/Helpers";

import Page from "components/page/Page";
import { Select } from "components/select/index";
import Input from "components/form/input/Input";
import TimePick from "components/timePick/TimePick";
import DataTable from "components/dataTable/DataTable";
import { isEqualWith } from "lodash";
import Search from "components/search/Search";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import ReactDOM from "react-dom";
import Callout from "components/callout/Callout";
import { ButtonNew as Button } from "components/buttonNew";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import "./teeSheetCustomerType.scss";

const DEFAULT_CREATE_CONFIG = {
  bookingCategoryId: -1,
  daysInAdvanceStart: "0",
  daysInAdvanceEnd: "7",
  openTime: "00:00",
  maxCurrentBookings: "",
  maxDailyBookings: "",
  bypassCreditCardRequired: false,
  maximumBookingSize: "",
};

export default function TeeSheetCustomerType() {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [customerTypeState, setCustomerTypeState] = useState<TCustomerType[] | undefined>(undefined);
  const [customerTypeStateBeforeChanges, setCustomerTypeStateBeforeChanges] = useState<TCustomerType[] | undefined>(
    undefined,
  );

  const [typeQuery, setTypeQuery] = useState<string>("");

  const [bookingCategoryState, setBookingCategoryState] = useState<IBookingCategory[]>([]);
  const { state: editAllModal, updateModal: updateEditAllModal } = useModal({
    clearOnClose: false,
    bookingCategoryId: -1,
    daysInAdvanceStart: "",
    daysInAdvanceEnd: "",
    openTime: "",
    maxCurrentBookings: "",
    maxDailyBookings: "",
    bypassCreditCardRequired: null as boolean | null,
    maximumBookingSize: "",
    selectedConfigIds: new Map(),
  });
  const { state: createMissingModal, updateModal: updateCreateMissingModal } = useModal({
    ...DEFAULT_CREATE_CONFIG,
    customerTypeIds: [] as number[],
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadBookingCategories(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomerTypes(source.token);
    return () => source.cancel();
  }, [typeQuery]);

  async function fetchCustomerTypes(token?: CancelToken, useGlobalLoader = false) {
    const res = await GetCustomerType(
      {
        search: typeQuery,
        application: "green_fee",
        configuration: true,
      },
      useGlobalLoader,
      token,
    );

    if (token && token.reason) {
      return null;
    }
    if (res.status !== StatusCode.OK) {
      return null;
    }

    return normalizeCustomerTypes(res.data);
  }

  async function loadCustomerTypes(token?: CancelToken) {
    setCustomerTypeState(undefined);
    setCustomerTypeStateBeforeChanges(undefined);

    const types = await fetchCustomerTypes(token, !token);
    if (!types) {
      return;
    }

    setCustomerTypeState(types);
    setCustomerTypeStateBeforeChanges(types);
  }

  async function reloadCustomerTypes() {
    ReactDOM.unstable_batchedUpdates(() => {
      setCustomerTypeState(undefined);
      setCustomerTypeStateBeforeChanges(undefined);
    });

    const types = await fetchCustomerTypes();
    if (!types) {
      dispatch(showError(t("Error loading customer type configurations")));
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setCustomerTypeState(types);
      setCustomerTypeStateBeforeChanges(types);
    });
  }

  async function loadBookingCategories(token?: CancelToken) {
    const res = await GetBookingCategories({}, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    setBookingCategoryState(res.data);
  }

  async function saveCustomerTypes() {
    /**
     * Allow users to understand when their changes will be properly saved.
     * - Single API call handles every type ('table-row') update.
     * - API call does not return an error if specific type is missing a required field.
     */
    if (!customerTypeState || activeRowError(customerTypeState, customerTypeStateBeforeChanges)) {
      if (customerTypeState) {
        dispatch(showError(t("Please fill in the missing inputs.")));
      }
      return;
    }

    const params: TPutCustomerTypeConfig[] = customerTypeState.filter(hasCustomerTypeConfig).map(stateType => {
      const configuration = stateType.configuration;
      return {
        id: configuration.id,
        booking_category_id:
          configuration.booking_category_id !== -1 ? configuration.booking_category_id ?? null : null,
        days_in_advance_start: configuration.days_in_advance_start
          ? configuration.days_in_advance_start.toString()
          : "0",
        days_in_advance_end: configuration.days_in_advance_end ? configuration.days_in_advance_end.toString() : "0",
        open_time: configuration.open_time && configuration.open_time.length !== 0 ? configuration.open_time : null,
        bypass_credit_card_required: Boolean(configuration.bypass_credit_card_required),
        max_current_bookings:
          configuration.max_current_bookings !== null ? Number(configuration.max_current_bookings) : null,
        max_daily_bookings: configuration.max_daily_bookings !== null ? Number(configuration.max_daily_bookings) : null,
        maximum_booking_size:
          configuration.maximum_booking_size !== null &&
          configuration.maximum_booking_size !== undefined &&
          String(configuration.maximum_booking_size).length !== 0
            ? Number(configuration.maximum_booking_size)
            : null,
      };
    });

    const res = await PutCustomerTypeConfiguration({ configurations: params }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving customer type configurations.")));
      return;
    }

    dispatch(showSuccess(t("Successfully saved customer type configurations.")));
    setCustomerTypeStateBeforeChanges(customerTypeState);
  }

  // typeIndex required because customerTypeState is array of objects we want changed
  function handleConfigurationChangeEvent(
    config: { id: string; value: string | boolean | undefined },
    typeIndex: number,
  ) {
    const { id, value } = config;

    setCustomerTypeState(prev =>
      (prev ?? []).map((type, index) => {
        if (index === typeIndex && hasCustomerTypeConfig(type)) {
          return {
            ...type,
            configuration: {
              ...type.configuration,
              [id as keyof TTeesheetCustomerTypeConfig]:
                typeof value === "boolean" ? value : !value || value.length === 0 ? null : value,
            },
          };
        }
        return type;
      }),
    );
  }

  //Save multiple customer type configurations. Only the checked configs will be saved
  async function saveSelectedCustomerTypeConfigs() {
    if (!customerTypeState) {
      return;
    }

    const selectedCustomerTypes = [...editAllModal.selectedConfigIds.keys()]
      .map((customerTypeId): TCustomerType | undefined => {
        return (
          customerTypeState.find(row => row.id === customerTypeId) ??
          (editAllModal.selectedConfigIds.get(customerTypeId) as TCustomerType | undefined)
        );
      })
      .filter(hasCustomerTypeConfig);

    //Only update the fields that have values in the inputs. Else, use the previous values
    const configParams: TPutCustomerTypeConfig[] = selectedCustomerTypes.map(selectedCustomerType => {
      const mergedConfiguration = {
        ...selectedCustomerType.configuration,
        id: selectedCustomerType.configuration.id,
        ...(editAllModal.bookingCategoryId && editAllModal.bookingCategoryId !== -1
          ? { booking_category_id: editAllModal.bookingCategoryId }
          : {}),
        ...(editAllModal.daysInAdvanceStart ? { days_in_advance_start: editAllModal.daysInAdvanceStart } : {}),
        ...(editAllModal.daysInAdvanceEnd ? { days_in_advance_end: editAllModal.daysInAdvanceEnd } : {}),
        ...(editAllModal.openTime ? { open_time: editAllModal.openTime } : {}),
        ...(editAllModal.maxCurrentBookings ? { max_current_bookings: editAllModal.maxCurrentBookings } : {}),
        ...(editAllModal.maxDailyBookings ? { max_daily_bookings: editAllModal.maxDailyBookings } : {}),
        ...(editAllModal.bypassCreditCardRequired !== null
          ? { bypass_credit_card_required: editAllModal.bypassCreditCardRequired }
          : {}),
        ...(editAllModal.maximumBookingSize ? { maximum_booking_size: editAllModal.maximumBookingSize } : {}),
      };

      return {
        id: mergedConfiguration.id,
        booking_category_id:
          mergedConfiguration.booking_category_id !== -1 ? mergedConfiguration.booking_category_id ?? null : null,
        days_in_advance_start: mergedConfiguration.days_in_advance_start
          ? mergedConfiguration.days_in_advance_start.toString()
          : "0",
        days_in_advance_end: mergedConfiguration.days_in_advance_end
          ? mergedConfiguration.days_in_advance_end.toString()
          : "0",
        open_time:
          mergedConfiguration.open_time && mergedConfiguration.open_time.length !== 0
            ? mergedConfiguration.open_time
            : null,
        bypass_credit_card_required: Boolean(mergedConfiguration.bypass_credit_card_required),
        max_current_bookings:
          mergedConfiguration.max_current_bookings !== null ? Number(mergedConfiguration.max_current_bookings) : null,
        max_daily_bookings:
          mergedConfiguration.max_daily_bookings !== null ? Number(mergedConfiguration.max_daily_bookings) : null,
        maximum_booking_size:
          mergedConfiguration.maximum_booking_size !== null &&
          mergedConfiguration.maximum_booking_size !== undefined &&
          String(mergedConfiguration.maximum_booking_size).length !== 0
            ? Number(mergedConfiguration.maximum_booking_size)
            : null,
      };
    });

    const res = await PutCustomerTypeConfiguration({ configurations: configParams }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving customer type configurations.")));
      return;
    }

    dispatch(showSuccess(t("Successfully saved customer type configurations.")));
    ReactDOM.unstable_batchedUpdates(() => {
      updateEditAllModal({
        isOpen: false,
        bookingCategoryId: -1,
        daysInAdvanceStart: "",
        daysInAdvanceEnd: "",
        openTime: "",
        maxCurrentBookings: "",
        maxDailyBookings: "",
        bypassCreditCardRequired: null,
        maximumBookingSize: "",
        selectedConfigIds: new Map(),
      });
    });
    await reloadCustomerTypes();
  }

  async function createCustomerTypeConfigs(
    customerTypeIds: number[],
    defaults?: Omit<TPostCustomerTypeConfig, "customer_type_ids">,
  ) {
    if (customerTypeIds.length === 0) {
      return;
    }

    const res = await PostCustomerTypeConfiguration(
      {
        customer_type_ids: customerTypeIds,
        ...defaults,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(
        showError(
          customerTypeIds.length === 1
            ? t("Error creating customer type configuration.")
            : t("Error creating customer type configurations."),
        ),
      );
      return;
    }

    dispatch(
      showSuccess(
        customerTypeIds.length === 1
          ? t("Successfully created customer type configuration.")
          : t("Successfully created customer type configurations."),
      ),
    );
    updateCreateMissingModal({ isOpen: false, customerTypeIds: [], ...DEFAULT_CREATE_CONFIG });
    await reloadCustomerTypes();
  }

  async function saveMissingCustomerTypeConfigs() {
    const defaults: Omit<TPostCustomerTypeConfig, "customer_type_ids"> = {
      booking_category_id: createMissingModal.bookingCategoryId !== -1 ? createMissingModal.bookingCategoryId : null,
      days_in_advance_start: createMissingModal.daysInAdvanceStart || "0",
      days_in_advance_end: createMissingModal.daysInAdvanceEnd || "7",
      open_time: createMissingModal.openTime || "00:00",
      bypass_credit_card_required: Boolean(createMissingModal.bypassCreditCardRequired),
      max_current_bookings: createMissingModal.maxCurrentBookings
        ? Number(createMissingModal.maxCurrentBookings)
        : null,
      max_daily_bookings: createMissingModal.maxDailyBookings ? Number(createMissingModal.maxDailyBookings) : null,
      maximum_booking_size: createMissingModal.maximumBookingSize
        ? Number(createMissingModal.maximumBookingSize)
        : null,
    };

    await createCustomerTypeConfigs(createMissingModal.customerTypeIds, defaults);
  }

  function handleConfigCheckbox(e: React.ChangeEvent<HTMLInputElement>, config: TCustomerType) {
    if (!hasCustomerTypeConfig(config)) {
      return;
    }
    const { checked } = e.target;
    const updatedSelectedConfigs = new Map([...editAllModal.selectedConfigIds]);
    if (checked) {
      updatedSelectedConfigs.set(config.id, config);
    } else {
      updatedSelectedConfigs.delete(config.id);
    }
    updateEditAllModal({ selectedConfigIds: updatedSelectedConfigs });
  }

  function handleSelectAllCheckbox() {
    const configurableTypes = customerTypeState?.filter(hasCustomerTypeConfig) ?? [];
    const updatedMap = new Map();
    configurableTypes.forEach(config => updatedMap.set(config.id, config));
    const allSelected = editAllModal?.selectedConfigIds?.size === configurableTypes.length && configurableTypes.length > 0;
    updateEditAllModal({ selectedConfigIds: allSelected ? new Map() : updatedMap });
  }

  const disableUpdatedSelected =
    (!editAllModal.bookingCategoryId || editAllModal.bookingCategoryId === -1) &&
    !editAllModal.daysInAdvanceStart &&
    !editAllModal.daysInAdvanceEnd &&
    !editAllModal.openTime &&
    !editAllModal?.maxCurrentBookings &&
    !editAllModal?.maxDailyBookings &&
    editAllModal.bypassCreditCardRequired === null &&
    !editAllModal?.maximumBookingSize;

  const missingCustomerTypes = customerTypeState?.filter(type => !hasCustomerTypeConfig(type)) ?? [];
  const configurableCustomerTypes = customerTypeState?.filter(hasCustomerTypeConfig) ?? [];
  const allConfigurableSelected =
    configurableCustomerTypes.length > 0 && editAllModal?.selectedConfigIds?.size === configurableCustomerTypes.length;
  const maxCurrentBookingsHelp = t(
    "Limits how many upcoming bookings a customer of this type can have at this facility. Leave blank for no limit.",
  );
  const maxDailyBookingsHelp = t(
    "Limits how many bookings a customer of this type can have at this facility on a single day. Leave blank for no limit.",
  );
  const maximumBookingSizeHelp = t(
    "Limits how many players this customer type can include in a single booking. Leaving it blank will use the size of the tee time.",
  );

  const dropdownOptions = [
    {
      type: "handler" as const,
      label: t("Update Selected"),
      icon: "pen-to-square" as const,
      handler: () => updateEditAllModal({ isOpen: true }),
    },
    ...(missingCustomerTypes.length > 1
      ? [
          {
            type: "handler" as const,
            label: t("Create Missing Configurations"),
            icon: "plus" as const,
            handler: () =>
              updateCreateMissingModal({
                isOpen: true,
                customerTypeIds: missingCustomerTypes.map(type => type.id),
                ...DEFAULT_CREATE_CONFIG,
              }),
          },
        ]
      : []),
  ];

  return (
    <Page
      title={t("Customer Type Configuration")}
      notificationBarProps={{
        isVisible: unsavedChangesExist(customerTypeState, customerTypeStateBeforeChanges),
        onAction: () => saveCustomerTypes(),
        onCancel: () => setCustomerTypeState(customerTypeStateBeforeChanges),
      }}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: dropdownOptions,
        },
      }}
    >
      <div className="mb-4">
        <Search
          placeholder={t("Search customer types...")}
          searchCallback={searchValue => setTypeQuery(searchValue)}
          historyKey="customer-type-config-query"
        />
      </div>
      {missingCustomerTypes.length > 1 && (
        <div className="mb-4">
          <Callout
            type="warning"
            title={t("Missing Configurations")}
            content={t("Some customer types do not have a configuration for this facility.")}
            actions={{
              primary: {
                label: t("Create Missing Configurations"),
                onClick: () =>
                  updateCreateMissingModal({
                    isOpen: true,
                    customerTypeIds: missingCustomerTypes.map(type => type.id),
                    ...DEFAULT_CREATE_CONFIG,
                  }),
              },
            }}
          />
        </div>
      )}
      <div className="tee-sheet-customer-type-table-scroll">
        <DataTable
          columns={[
            {
              label: "",
              content: (
                <Checkbox
                  size="small"
                  checked={allConfigurableSelected}
                  onChange={() => handleSelectAllCheckbox()}
                  disabled={configurableCustomerTypes.length === 0}
                />
              ),
              width: "10%",
            },
            { label: t("Customer Type"), width: "10%" },
            { label: t("Booking Category"), width: "15%" },
            { label: t("Booking Window Start"), width: "10%" },
            { label: t("Booking Window End"), width: "10%" },
            { label: t("Booking Window Open Time"), width: "15%" },
            { label: t("Bypass Credit Card Required"), width: "10%" },
            {
              label: t("Max Current Bookings"),
              content: (
                <TableHeaderHelp
                  label={t("Max Current Bookings")}
                  tooltipId="max-current-bookings-tooltip"
                  helpText={maxCurrentBookingsHelp}
                />
              ),
              width: "10%",
            },
            {
              label: t("Max Daily Bookings"),
              content: (
                <TableHeaderHelp
                  label={t("Max Daily Bookings")}
                  tooltipId="max-daily-bookings-tooltip"
                  helpText={maxDailyBookingsHelp}
                />
              ),
              width: "10%",
            },
            {
              label: t("Maximum Booking Size"),
              content: (
                <TableHeaderHelp
                  label={t("Maximum Booking Size")}
                  tooltipId="maximum-booking-size-tooltip"
                  helpText={maximumBookingSizeHelp}
                />
              ),
              width: "10%",
            },
          ]}
          loading={!customerTypeState}
          overflow
        >
          {customerTypeState?.map((type, typeIndex) => {
            const missingConfig = !hasCustomerTypeConfig(type);

            return (
              <tr key={type.id}>
                <td>
                  <Checkbox
                    size="small"
                    checked={editAllModal?.selectedConfigIds?.has(type.id) ?? false}
                    onChange={e => handleConfigCheckbox(e, type)}
                    disabled={missingConfig}
                  />
                </td>
                <td>
                  <p>{type.title}</p>
                  <p>{type.subtitle}</p>
                </td>

                {missingConfig ? (
                  <>
                    <td>
                      <p className="tee-sheet-customer-type-missing-config-label">{t("No configuration")}</p>
                    </td>
                    <td>
                      <Button size="small" type="secondary" onClick={() => void createCustomerTypeConfigs([type.id])}>
                        {t("Create Configuration")}
                      </Button>
                    </td>
                    <td colSpan={6} />
                  </>
                ) : (
                  <>
                    <td>
                      <Select
                        onChange={value =>
                          handleConfigurationChangeEvent({ id: "booking_category_id", value: value }, typeIndex)
                        }
                        defaultValue={type.configuration?.booking_category_id}
                        dropDownPositionTop={typeIndex + 1 === customerTypeState?.length && typeIndex !== 0}
                        usePortal
                        error={
                          !isEqualWith(
                            type.configuration,
                            customerTypeStateBeforeChanges?.find(preservedType => preservedType.id === type.id)
                              ?.configuration,
                          ) && type.configuration?.booking_category_id === -1
                        }
                      >
                        <Select.Option value={-1}>{t("None")}</Select.Option>
                        {bookingCategoryState.map((bookingCategory, bookingCategoryIndex) => {
                          return (
                            <Select.Option key={bookingCategoryIndex} value={bookingCategory.id}>
                              {bookingCategory.title}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </td>

                    <td>
                      <Input
                        className="-mt-4"
                        value={type.configuration?.days_in_advance_start ?? ""}
                        id="days_in_advance_start"
                        onChange={e =>
                          handleConfigurationChangeEvent({ id: e.target.id, value: e.target.value }, typeIndex)
                        }
                        error={
                          !isEqualWith(
                            type.configuration,
                            customerTypeStateBeforeChanges?.find(preservedType => preservedType.id === type.id)
                              ?.configuration,
                          ) &&
                          type.configuration?.days_in_advance_start !== 0 &&
                          !type.configuration?.days_in_advance_start
                        }
                        suffix={t("Days")}
                      />
                    </td>
                    <td>
                      <Input
                        className="-mt-4"
                        value={type.configuration?.days_in_advance_end ?? ""}
                        id="days_in_advance_end"
                        onChange={e =>
                          handleConfigurationChangeEvent({ id: e.target.id, value: e.target.value }, typeIndex)
                        }
                        error={
                          !isEqualWith(
                            type.configuration,
                            customerTypeStateBeforeChanges?.find(preservedType => preservedType.id === type.id)
                              ?.configuration,
                          ) &&
                          type.configuration?.days_in_advance_end !== 0 &&
                          !type.configuration?.days_in_advance_end
                        }
                        suffix={t("Days")}
                      />
                    </td>
                    <td>
                      <TimePick
                        value={type.configuration?.open_time ?? ""}
                        onChange={timeString =>
                          handleConfigurationChangeEvent({ id: "open_time", value: timeString }, typeIndex)
                        }
                        size="large"
                        align="right"
                        positionTop={typeIndex + 1 === customerTypeState?.length && typeIndex !== 0}
                      />
                    </td>
                    <td>
                      <Checkbox
                        size="small"
                        checked={Boolean(type.configuration?.bypass_credit_card_required)}
                        onChange={e =>
                          handleConfigurationChangeEvent(
                            { id: "bypass_credit_card_required", value: e.target.checked },
                            typeIndex,
                          )
                        }
                      />
                    </td>
                    <td>
                      <Input
                        id="max_current_bookings"
                        value={
                          type.configuration?.max_current_bookings !== null
                            ? type.configuration?.max_current_bookings
                            : null
                        }
                        onChange={e =>
                          handleConfigurationChangeEvent({ id: e.target.id, value: e.target.value }, typeIndex)
                        }
                        className="-mt-4"
                      />
                    </td>
                    <td>
                      <Input
                        id="max_daily_bookings"
                        value={
                          type.configuration?.max_daily_bookings !== null
                            ? type.configuration?.max_daily_bookings
                            : null
                        }
                        onChange={e =>
                          handleConfigurationChangeEvent({ id: e.target.id, value: e.target.value }, typeIndex)
                        }
                        className="-mt-4"
                      />
                    </td>
                    <td>
                      <Input
                        id="maximum_booking_size"
                        value={
                          type.configuration?.maximum_booking_size !== null &&
                          type.configuration?.maximum_booking_size !== undefined
                            ? type.configuration?.maximum_booking_size
                            : null
                        }
                        onChange={e =>
                          handleConfigurationChangeEvent({ id: e.target.id, value: e.target.value }, typeIndex)
                        }
                        className="-mt-4"
                      />
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </DataTable>
      </div>

      <Portal isMounted={editAllModal.isOpen}>
        <Sheet
          open={editAllModal.isOpen}
          size="small"
          title={t("Update Selected Customer Type Configurations")}
          onCancel={() =>
            updateEditAllModal({
              isOpen: false,
              bookingCategoryId: -1,
              daysInAdvanceStart: "",
              daysInAdvanceEnd: "",
              openTime: "",
              maxCurrentBookings: "",
              maxDailyBookings: "",
              bypassCreditCardRequired: null,
              maximumBookingSize: "",
            })
          }
          onOk={saveSelectedCustomerTypeConfigs}
          okDisabled={disableUpdatedSelected}
          okText={t("Update")}
          overflow
        >
          {editAllModal?.selectedConfigIds?.size > 0 ? (
            <FormLayout>
              <FormLayout.Group>
                <Select
                  onChange={(bookingCategoryId: number) => updateEditAllModal({ bookingCategoryId })}
                  defaultValue={-1}
                  label={t("Booking Category")}
                >
                  <Select.Option value={-1}>{t("None")}</Select.Option>
                  {bookingCategoryState.map((bookingCategory, bookingCategoryIndex) => {
                    return (
                      <Select.Option key={bookingCategoryIndex} value={bookingCategory.id}>
                        {bookingCategory.title}
                      </Select.Option>
                    );
                  })}
                </Select>
                <Input
                  value={editAllModal.daysInAdvanceStart}
                  onChange={e => updateEditAllModal({ daysInAdvanceStart: e.target.value })}
                  label={t("Days In Advance Start")}
                  type="number"
                />
                <Input
                  value={editAllModal.daysInAdvanceEnd}
                  onChange={e => updateEditAllModal({ daysInAdvanceEnd: e.target.value })}
                  label={t("Days In Advance End")}
                  type="number"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TimePick
                  value={editAllModal.openTime}
                  onChange={timeString => updateEditAllModal({ openTime: timeString })}
                  size="large"
                  label={t("Open Time")}
                />
                <Input
                  value={editAllModal.maxCurrentBookings}
                  onChange={e => updateEditAllModal({ maxCurrentBookings: e.target.value })}
                  label={t("Max Current Bookings")}
                  type="number"
                  tooltipText={maxCurrentBookingsHelp}
                />
                <Input
                  value={editAllModal.maxDailyBookings}
                  onChange={e => updateEditAllModal({ maxDailyBookings: e.target.value })}
                  label={t("Max Daily Bookings")}
                  type="number"
                  tooltipText={maxDailyBookingsHelp}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Checkbox
                  size="small"
                  label={t("Bypass Credit Card Required")}
                  checked={editAllModal.bypassCreditCardRequired === true}
                  onChange={e => updateEditAllModal({ bypassCreditCardRequired: e.target.checked })}
                />
                <Input
                  value={editAllModal.maximumBookingSize}
                  onChange={e => updateEditAllModal({ maximumBookingSize: e.target.value })}
                  label={t("Maximum Booking Size")}
                  type="number"
                  tooltipText={maximumBookingSizeHelp}
                />
              </FormLayout.Group>
            </FormLayout>
          ) : (
            <Callout
              title={t("Customer Type Selection Needed")}
              content={t("To proceed, please select at least one customer type using the checkboxes on each row")}
              type="error"
            />
          )}
        </Sheet>
      </Portal>

      <Portal isMounted={createMissingModal.isOpen}>
        <Sheet
          open={createMissingModal.isOpen}
          size="small"
          title={t("Create Missing Configurations")}
          onCancel={() => updateCreateMissingModal({ isOpen: false, customerTypeIds: [], ...DEFAULT_CREATE_CONFIG })}
          onOk={saveMissingCustomerTypeConfigs}
          okText={t("Create")}
          overflow
        >
          <div className="mb-4">
            <Callout
              type="info"
              title={t("Default Values")}
              content={t(
                "These default values will be applied to each customer type that is missing a configuration.",
              )}
            />
          </div>
          <FormLayout>
            <FormLayout.Group>
              <Select
                onChange={(bookingCategoryId: number) => updateCreateMissingModal({ bookingCategoryId })}
                defaultValue={createMissingModal.bookingCategoryId}
                label={t("Booking Category")}
              >
                <Select.Option value={-1}>{t("None")}</Select.Option>
                {bookingCategoryState.map((bookingCategory, bookingCategoryIndex) => {
                  return (
                    <Select.Option key={bookingCategoryIndex} value={bookingCategory.id}>
                      {bookingCategory.title}
                    </Select.Option>
                  );
                })}
              </Select>
              <Input
                value={createMissingModal.daysInAdvanceStart}
                onChange={e => updateCreateMissingModal({ daysInAdvanceStart: e.target.value })}
                label={t("Days In Advance Start")}
                type="number"
              />
              <Input
                value={createMissingModal.daysInAdvanceEnd}
                onChange={e => updateCreateMissingModal({ daysInAdvanceEnd: e.target.value })}
                label={t("Days In Advance End")}
                type="number"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TimePick
                value={createMissingModal.openTime}
                onChange={timeString => updateCreateMissingModal({ openTime: timeString })}
                size="large"
                label={t("Open Time")}
              />
              <Input
                value={createMissingModal.maxCurrentBookings}
                onChange={e => updateCreateMissingModal({ maxCurrentBookings: e.target.value })}
                label={t("Max Current Bookings")}
                type="number"
                tooltipText={maxCurrentBookingsHelp}
              />
              <Input
                value={createMissingModal.maxDailyBookings}
                onChange={e => updateCreateMissingModal({ maxDailyBookings: e.target.value })}
                label={t("Max Daily Bookings")}
                type="number"
                tooltipText={maxDailyBookingsHelp}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                size="small"
                label={t("Bypass Credit Card Required")}
                checked={createMissingModal.bypassCreditCardRequired === true}
                onChange={e => updateCreateMissingModal({ bypassCreditCardRequired: e.target.checked })}
              />
              <Input
                value={createMissingModal.maximumBookingSize}
                onChange={e => updateCreateMissingModal({ maximumBookingSize: e.target.value })}
                label={t("Maximum Booking Size")}
                type="number"
                tooltipText={maximumBookingSizeHelp}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
    </Page>
  );
}

function TableHeaderHelp({
  label,
  tooltipId,
  helpText,
}: {
  label: string;
  tooltipId: string;
  helpText: string;
}) {
  return (
    <div className="tee-sheet-customer-type-column-label">
      <p className="table-label">
        {label}
        <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{helpText}</Tooltip>}>
          <FontAwesomeIcon icon={faQuestionCircle} className="tee-sheet-customer-type-column-help" />
        </OverlayTrigger>
      </p>
    </div>
  );
}

function hasCustomerTypeConfig(
  type?: TCustomerType | null,
): type is TCustomerType & { configuration: TTeesheetCustomerTypeConfig } {
  return Boolean(type?.configuration?.id);
}

function normalizeCustomerTypes(types: TCustomerType[]): TCustomerType[] {
  return types.map(type => {
    if (!type.configuration?.id) {
      return { ...type, configuration: undefined };
    }

    return {
      ...type,
      configuration: {
        ...type.configuration,
        id: type.configuration.id,
        booking_category_id: type.configuration.booking_category_id ?? -1,
      },
    };
  });
}

/** Checks for change to have been made to that row & if the row itself has valid inputs. */
function activeRowError(current?: TCustomerType[], preserved?: TCustomerType[]) {
  if (!current || !preserved) {
    return false;
  }
  const invalidRows: number[] = [];

  current.map(type => {
    if (!hasCustomerTypeConfig(type)) {
      return;
    }

    const changesMade = !isEqualWith(
      type.configuration,
      preserved.find(preservedType => preservedType.id === type.id)?.configuration,
    );

    const valid = validRow(type.id, current);

    if (changesMade && !valid) {
      invalidRows.push(type.id);
    }
  });

  if (invalidRows.length === 0) {
    return false;
  }
  return true;
}

/**
 * Checking For:
 * - booking_category_id
 * - days_in_advance_start
 * - days_in_advance_end
 */
const validRow = (typeId: number, state: TCustomerType[]) => {
  const rowsNotValid: number[] = [];
  const chosenType = state.find(value => value.id === typeId);

  if (!hasCustomerTypeConfig(chosenType)) {
    return true;
  }

  // validate config inputs...
  if (
    chosenType?.configuration &&
    (chosenType.configuration.booking_category_id === -1 ||
      (chosenType.configuration.days_in_advance_start !== 0 && !chosenType.configuration.days_in_advance_start) ||
      (chosenType.configuration.days_in_advance_end !== 0 && !chosenType.configuration.days_in_advance_end))
  ) {
    rowsNotValid.push(chosenType.id);
  }

  if (rowsNotValid.length > 0) {
    return false;
  }

  return true;
};
